import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import {
  normalizeTableViewConfig,
  TableViewConfig,
} from "../../../hooks/useTableViewConfig";
import {
  useCreateTableViewMutation,
  useDeleteTableViewMutation,
  useGetTableViewsQuery,
  useUpdateTableViewMutation,
} from "../../../store/apis/tableViewsApi";
import { setToastMessage } from "../../../store/slices/app/appSlice";

/** A built-in view type: a column set, same for everybody. */
export interface ViewPreset {
  id: string;
  name: string;
  visibleColumnIds: string[];
}

interface ViewsMenuProps {
  tableKey: string;
  config: TableViewConfig;
  defaults: TableViewConfig;
  presets: ViewPreset[];
  /** "preset:<id>" or the id of a saved view. */
  activeViewId: string;
  onApply: (config: TableViewConfig, activeViewId: string) => void;
}

const presetKey = (id: string) => `preset:${id}`;

// What a preset decides; filters and widths stay as the user has them.
const columnsPart = (c: TableViewConfig) =>
  JSON.stringify([c.visibleColumnIds, c.columnOrder, c.sort]);

const getErrorMessage = (error: unknown): string | null => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string } }).data;
    if (data && typeof data.message === "string") return data.message;
  }
  return null;
};

/**
 * Picks the table's view: built-in view types (column sets) or the user's own
 * saved views (columns, order, widths, filters and sort). Saved views live in
 * the backend, so they follow the user to any computer.
 */
const ViewsMenu: React.FC<ViewsMenuProps> = ({
  tableKey,
  config,
  defaults,
  presets,
  activeViewId,
  onApply,
}) => {
  const dispatch = useDispatch();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [nameDialog, setNameDialog] = useState<"create" | "rename" | null>(
    null
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data } = useGetTableViewsQuery(tableKey);
  const [createView, { isLoading: isCreating }] = useCreateTableViewMutation();
  const [updateView, { isLoading: isUpdating }] = useUpdateTableViewMutation();
  const [deleteView, { isLoading: isDeleting }] = useDeleteTableViewMutation();

  const views = data?.data ?? [];
  const activePreset = presets.find((p) => presetKey(p.id) === activeViewId);
  const activeView = views.find((v) => v.id === activeViewId);

  const presetConfig = (p: ViewPreset): TableViewConfig => ({
    ...config,
    visibleColumnIds: p.visibleColumnIds,
    columnOrder: [],
    sort: null,
  });

  const isModified = activeView
    ? JSON.stringify(normalizeTableViewConfig(activeView.config, defaults)) !==
      JSON.stringify(config)
    : activePreset
      ? columnsPart(presetConfig(activePreset)) !== columnsPart(config)
      : false;

  const label = activeView?.name ?? activePreset?.name ?? "Vista";

  const toast = (message: string) =>
    dispatch(setToastMessage({ message, severity: "success" }));

  const close = () => setAnchor(null);

  const openNameDialog = (mode: "create" | "rename") => {
    setName(mode === "rename" ? (activeView?.name ?? "") : "");
    setError(null);
    setNameDialog(mode);
    close();
  };

  const saveName = async () => {
    setError(null);
    try {
      if (nameDialog === "create") {
        const res = await createView({
          tableKey,
          name,
          config: config as unknown as Record<string, unknown>,
        }).unwrap();
        if (res.data) onApply(config, res.data.id);
        toast("Vista guardada");
      } else if (activeView) {
        await updateView({
          id: activeView.id,
          tableKey,
          body: { name },
        }).unwrap();
        toast("Vista renombrada");
      }
      setNameDialog(null);
    } catch (err) {
      setError(getErrorMessage(err) || "No se ha podido guardar la vista");
    }
  };

  const saveChanges = async () => {
    if (!activeView) return;
    close();
    try {
      await updateView({
        id: activeView.id,
        tableKey,
        body: { config: config as unknown as Record<string, unknown> },
      }).unwrap();
      toast(`Cambios guardados en «${activeView.name}»`);
    } catch (err) {
      dispatch(
        setToastMessage({
          message:
            getErrorMessage(err) || "No se han podido guardar los cambios",
          severity: "error",
        })
      );
    }
  };

  const remove = async () => {
    if (!activeView) return;
    try {
      await deleteView({ id: activeView.id, tableKey }).unwrap();
      const fallback = presets[0];
      onApply(presetConfig(fallback), presetKey(fallback.id));
      setConfirmDelete(false);
      toast("Vista eliminada");
    } catch (err) {
      setError(getErrorMessage(err) || "No se ha podido eliminar la vista");
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ whiteSpace: "nowrap", textTransform: "none" }}
      >
        {label}
        {isModified ? " (modificada)" : ""}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <ListSubheader>Tipos de vista</ListSubheader>
        {presets.map((p) => (
          <MenuItem
            key={p.id}
            onClick={() => {
              onApply(presetConfig(p), presetKey(p.id));
              close();
            }}
          >
            <ListItemIcon>
              {activeViewId === presetKey(p.id) && (
                <CheckIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={p.name} />
          </MenuItem>
        ))}
        <ListSubheader>Mis vistas</ListSubheader>
        {views.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              inset
              primary="Aún no tienes vistas guardadas"
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </MenuItem>
        )}
        {views.map((v) => (
          <MenuItem
            key={v.id}
            onClick={() => {
              onApply(normalizeTableViewConfig(v.config, defaults), v.id);
              close();
            }}
          >
            <ListItemIcon>
              {activeViewId === v.id && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={v.name} />
          </MenuItem>
        ))}
        <Divider />
        {activeView && isModified && (
          <MenuItem onClick={saveChanges} disabled={isUpdating}>
            <ListItemIcon>
              <SaveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={`Guardar cambios en «${activeView.name}»`} />
          </MenuItem>
        )}
        <MenuItem onClick={() => openNameDialog("create")}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Guardar como vista nueva…" />
        </MenuItem>
        {activeView && (
          <MenuItem onClick={() => openNameDialog("rename")}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Renombrar vista…" />
          </MenuItem>
        )}
        {activeView && (
          <MenuItem
            onClick={() => {
              setError(null);
              setConfirmDelete(true);
              close();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Eliminar vista" />
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={nameDialog !== null}
        onClose={() => setNameDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {nameDialog === "create" ? "Guardar vista" : "Renombrar vista"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) void saveName();
            }}
            helperText={
              nameDialog === "create"
                ? "Guarda columnas, orden, anchos, filtros y ordenación actuales."
                : undefined
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || isCreating || isUpdating}
            onClick={() => void saveName()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar vista</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          ¿Eliminar la vista «{activeView?.name}»? No se puede deshacer.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={() => void remove()}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewsMenu;
